import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGames, useUsers } from '@/hooks/useShared';
import { usePendingTransactions } from '@/hooks/usePendingTransactions';
import { calculatePlayerDebts } from '@/services/debts';
import { InsightsSubNav } from '@/components/insights/InsightsSubNav';
import { LoadingSpinner } from '@/components/ui';

export function DebtsPage() {
  const { transactions, loading: txLoading } = usePendingTransactions();
  const { games, loading: gamesLoading } = useGames();
  const { users, loading: usersLoading } = useUsers();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState<'owed' | 'owing'>('owed');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { playerDebts, gameMap } = useMemo(
    () => calculatePlayerDebts(transactions, users, games),
    [transactions, users, games],
  );

  const sorted = useMemo(() => {
    return [...playerDebts].sort((a, b) =>
      sortBy === 'owed' ? b.totalOwed - a.totalOwed : b.totalOwing - a.totalOwing,
    );
  }, [playerDebts, sortBy]);

  if (txLoading || gamesLoading || usersLoading) {
    return <LoadingSpinner message="Loading settlement data..." />;
  }

  return (
    <div className="max-w-[400px] mx-auto">
      <div className="bg-[#e8f5f0] border border-[#006855]/10 rounded-2xl p-5 mb-4">
        <div className="text-[10px] font-black uppercase tracking-[2px] text-[#006855]/70 mb-1">SETTLEMENTS</div>
        <h1 className="text-[22px] font-extrabold text-[#2c2f30] leading-tight">Outstanding</h1>
      </div>

      <InsightsSubNav />

      {/* Sort toggle */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        <button
          onClick={() => setSortBy('owed')}
          className={`px-[16px] py-[8px] rounded-full border-none cursor-pointer text-[13px] font-bold transition-colors ${
            sortBy === 'owed'
              ? 'bg-brand-primary text-bg-app'
              : 'bg-[#e8f5f0] text-text-muted hover:bg-[#d4e8e0]'
          }`}
        >
          Owes Most
        </button>
        <button
          onClick={() => setSortBy('owing')}
          className={`px-[16px] py-[8px] rounded-full border-none cursor-pointer text-[13px] font-bold transition-colors ${
            sortBy === 'owing'
              ? 'bg-brand-primary text-bg-app'
              : 'bg-[#e8f5f0] text-text-muted hover:bg-[#d4e8e0]'
          }`}
        >
          Owed Most
        </button>
      </div>

      {sorted.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-text-muted font-medium text-sm">No outstanding settlements</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {sorted.map(player => {
            const isExpanded = expandedId === player.userId;
            const games = gameMap.get(player.userId) ?? [];

            return (
              <div key={player.userId} className="bg-white rounded-xl shadow-botanical overflow-hidden">
                {/* Player row */}
                <div
                  onClick={() => setExpandedId(isExpanded ? null : player.userId)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 14px',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                  className="hover:bg-bg-app transition-colors"
                >
                  <div className="w-9 h-9 rounded-full bg-brand-primary overflow-hidden flex-shrink-0">
                    <div className="w-full h-full flex items-center justify-center text-[16px] font-extrabold text-bg-app">
                      {player.name.charAt(0)}
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="font-semibold text-text-main text-[15px] truncate">{player.name}</div>
                  </div>
                  {player.totalOwed > 0 && (
                    <div style={{ fontSize: 14, fontWeight: 800, color: '#ef4444', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      -${player.totalOwed.toFixed(2)}
                    </div>
                  )}
                  {player.totalOwing > 0 && (
                    <div style={{ fontSize: 14, fontWeight: 800, color: '#22c55e', textAlign: 'right', whiteSpace: 'nowrap', marginLeft: 8 }}>
                      +${player.totalOwing.toFixed(2)}
                    </div>
                  )}
                </div>

                {/* Expanded: game detail */}
                {isExpanded && (
                  <div style={{ borderTop: '1px solid #e5e7eb', padding: '12px 14px', background: '#f9fafb' }}>
                    {games.map(game => {
                      const hasOwes = game.owes.length > 0;
                      const hasOwed = game.owed.length > 0;
                      if (!hasOwes && !hasOwed) return null;
                      return (
                        <div
                          key={game.gameId}
                          onClick={() => navigate(`/game/${game.gameId}`)}
                          className="cursor-pointer hover:bg-[#e8f5f0]/60 rounded-lg transition-colors -mx-1 px-2 py-1.5"
                          style={{ marginBottom: 10 }}
                        >
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#2c2f30', marginBottom: 4 }}>
                            {game.date} · {game.venue}
                          </div>
                          {game.owes.map((debt, i) => (
                            <div key={`owes-${i}`} style={{ fontSize: 12, color: '#6b7280', paddingLeft: 12, marginBottom: 1 }}>
                              Owes <span style={{ fontWeight: 600, color: '#ef4444' }}>${debt.amount.toFixed(2)}</span> to {debt.toName}
                            </div>
                          ))}
                          {game.owed.map((debt, i) => (
                            <div key={`owed-${i}`} style={{ fontSize: 12, color: '#6b7280', paddingLeft: 12, marginBottom: 1 }}>
                              Owed <span style={{ fontWeight: 600, color: '#22c55e' }}>${debt.amount.toFixed(2)}</span> from {debt.fromName}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                    {games.length === 0 && (
                      <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', padding: 8 }}>No pending transactions</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
